// ─────────────────────────────────────────────────────────────────────
//  NewsAppify plans, build stages and billing helpers used across the
//  staff, admin and customer pages. Keep in step with functions/src/index.ts.
// ─────────────────────────────────────────────────────────────────────

// Stripe publishable key (safe to expose). It must come from the SAME
// Stripe account as the STRIPE_SECRET_KEY secret set on the functions.
export const STRIPE_PUBLISHABLE_KEY = 'pk_live_51UTg3CEM0pYoLDqcnfP1pnRwiOyFdhwbXYXKSSpcpAk7OtQJPazLp7DUEnV2RkYAdnx7qn49QDdYP0STlGOj0BHm00K9W5x7HV'

export const PLANS = {
  community: { key: 'community', name: 'Community', monthly: 39900, yearly: 399000, setup: 150000, emails: 25000, fits: 'Under 10,000 residents' },
  town: { key: 'town', name: 'Town', monthly: 79900, yearly: 799000, setup: 200000, emails: 100000, fits: '10,000 – 50,000 residents' },
  city: { key: 'city', name: 'City', monthly: 149900, yearly: 1499000, setup: 250000, emails: 300000, fits: '50,000+ residents', from: true },
}
export const PLAN_LIST = Object.values(PLANS)
export const TRIAL_DAYS = 30
export const TRIAL_EMAIL_CAP = 2000
export const OVERAGE_PER_1000 = 2

/** Lead "planInterest" ('Community') or a key ('community') → plan key. */
export const planKey = (v) => {
  const k = String(v || '').toLowerCase()
  return PLANS[k] ? k : 'community'
}
/** Price per period in cents. City can be priced above its floor. */
export function planAmount(key, interval, customMonthlyCents) {
  const p = PLANS[planKey(key)]
  if (p.key === 'city' && customMonthlyCents > p.monthly) return interval === 'year' ? customMonthlyCents * 10 : customMonthlyCents
  return interval === 'year' ? p.yearly : p.monthly
}
/** Monthly recurring value of a customer record, in cents. */
export function monthlyValue(c) {
  if (!c) return 0
  const amount = Number(c.planAmountCents) || planAmount(c.plan, c.billingInterval, c.customMonthlyCents)
  return c.billingInterval === 'year' ? Math.round(amount / 12) : amount
}

export const money = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: cents % 100 ? 2 : 0 }).format((Number(cents) || 0) / 100)
export const moneyExact = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((Number(cents) || 0) / 100)

export const SUB_STATUS = {
  trialing: { label: 'Free trial', tone: 'brass' },
  active: { label: 'Active', tone: 'green' },
  past_due: { label: 'Past due', tone: 'red' },
  unpaid: { label: 'Unpaid', tone: 'red' },
  incomplete: { label: 'Incomplete', tone: 'red' },
  canceled: { label: 'Canceled', tone: '' },
}
export const subStatus = (s) => SUB_STATUS[s] || SUB_STATUS.trialing

export const INVOICE_TONE = { paid: 'green', open: 'brass', draft: '', void: '', uncollectible: 'red' }

// ── Build stages ─────────────────────────────────────────────────────
export const STAGES = ['Not Started', 'Designing', 'Building', 'Testing', 'App Store Review', 'Live']
export const STAGE_HELP = {
  'Not Started': 'Account created. Kickoff call next.',
  'Designing': 'Name, colors, sections and home screen.',
  'Building': 'News sections, newsletters, events, staff dashboards.',
  'Testing': 'Real phones, real inboxes, real data.',
  'App Store Review': 'Submitted to Apple and Google Play.',
  'Live': 'Residents can download it. Requests open.',
}
// Older records may use the previous stage names.
const LEGACY = { Developing: 'Building', Completed: 'Live' }
export const normalizeStage = (s) => LEGACY[s] || (STAGES.includes(s) ? s : 'Not Started')
export const stageIndex = (s) => STAGES.indexOf(normalizeStage(s))
export const stagePercent = (s) => Math.round((stageIndex(s) / (STAGES.length - 1)) * 100)

// ── Team CSV ─────────────────────────────────────────────────────────
export const TEAM_TYPES = [
  { key: 'Staff', help: 'Reporters, editors and town staff who publish' },
  { key: 'Admin', help: 'Full control of the app, settings and people' },
  { key: 'Contributor', help: 'Columnists, schools, clubs — submit for review' },
  { key: 'Resident', help: 'Readers to invite and sign up for newsletters' },
]
export const TEAM_CSV_COLUMNS = ['type', 'email', 'fullName', 'title', 'department', 'phone', 'neighborhood']
export const TEAM_CSV_SAMPLE = [
  TEAM_CSV_COLUMNS.join(','),
  'Admin,clerk@yourtown.gov,Dana Ruiz,Town Clerk,Town Hall,555-0101,',
  'Staff,editor@yourtown.gov,Marcus Lee,Communications Director,Communications,555-0102,',
  'Contributor,coach@yourschools.org,Pat Kim,Athletic Director,Schools,,',
  'Resident,jordan@example.com,Jordan Ellis,,,,Ward 3',
].join('\n')

export const tsDate = (t) => {
  if (!t) return null
  if (t.toDate) return t.toDate()
  if (t.seconds) return new Date(t.seconds * 1000)
  if (typeof t === 'number') return new Date(t < 1e12 ? t * 1000 : t)
  const d = new Date(t)
  return isNaN(d) ? null : d
}
export const fmtDate = (t, opts = { month: 'short', day: 'numeric', year: 'numeric' }) => {
  const d = tsDate(t)
  return d ? d.toLocaleDateString('en-US', opts) : '—'
}
export const daysUntil = (t) => {
  const d = tsDate(t)
  return d ? Math.ceil((d.getTime() - Date.now()) / 864e5) : null
}
/** Firebase callable errors → a readable sentence. */
export const fnError = (e) => String((e && e.message) || 'Something went wrong.').replace(/^Firebase:\s*/, '').replace(/\s*\(functions\/[a-z-]+\)\.?$/, '')

// ── App colors a customer can pick (Settings → Logo & colors) ────────
export const APP_COLORS = [
  { value: 'navy', label: 'Newsroom navy', hex: '#14315E' },
  { value: 'brass', label: 'Brass', hex: '#B8925A' },
  { value: 'red', label: 'Press red', hex: '#A31F1F' },
  { value: 'forest', label: 'Town green', hex: '#2F6B45' },
  { value: 'harbor', label: 'Harbor teal', hex: '#1F6F78' },
  { value: 'slate', label: 'Slate', hex: '#3E4A5C' },
  { value: 'plum', label: 'Plum', hex: '#5B3A6B' },
]
/** A stored colorPreference ('navy' or a custom '#hex') → { label, hex }. */
export const appColor = (v) => APP_COLORS.find(c => c.value === v) || (/^#[0-9a-f]{6}$/i.test(v || '') ? { value: v, label: 'Custom', hex: v } : APP_COLORS[0])